import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { RefreshCw, Pencil, Check, X, Clock, Coffee, AlertTriangle, CheckCircle2 } from "lucide-react";
import ShiftReports from "@/pages/ShiftReports";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const COMMON_TZ = [
  "Asia/Kolkata", "Asia/Singapore", "Asia/Dubai", "Asia/Tokyo",
  "Europe/London", "Europe/Berlin",
  "America/New_York", "America/Chicago", "America/Los_Angeles",
  "Australia/Sydney", "UTC",
];

const EMPTY_SCHEDULE = {
  start_time: "09:00",
  end_time: "18:00",
  break_minutes: 60,
  timezone: "Asia/Kolkata",
  days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
};

function fmt(secs) {
  secs = Math.max(0, Math.floor(secs || 0));
  const h = String(Math.floor(secs / 3600)).padStart(2, "0");
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
  const s = String(secs % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function durationSeconds(start, end) {
  if (!start) return 0;
  const a = new Date(start).getTime();
  const b = end ? new Date(end).getTime() : Date.now();
  return Math.max(0, (b - a) / 1000);
}

function ScheduleEditor({ value, onSave, onCancel }) {
  const [draft, setDraft] = useState({ ...EMPTY_SCHEDULE, ...(value || {}) });
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const toggleDay = (d) => {
    setDraft((s) => ({
      ...s,
      days: s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d],
    }));
  };
  return (
    <div className="border border-border bg-secondary/30 p-4 rounded-sm space-y-3" data-testid="schedule-editor">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="meta-label">Start Time</label>
          <Input
            data-testid="schedule-start-input"
            type="time"
            value={draft.start_time || ""}
            onChange={(e) => set("start_time", e.target.value)}
            className="rounded-sm h-9 mt-1 font-mono"
          />
        </div>
        <div>
          <label className="meta-label">End Time</label>
          <Input
            data-testid="schedule-end-input"
            type="time"
            value={draft.end_time || ""}
            onChange={(e) => set("end_time", e.target.value)}
            className="rounded-sm h-9 mt-1 font-mono"
          />
        </div>
        <div>
          <label className="meta-label">Break (minutes)</label>
          <Input
            data-testid="schedule-break-input"
            type="number"
            min={0}
            max={480}
            value={draft.break_minutes ?? 0}
            onChange={(e) => set("break_minutes", parseInt(e.target.value || "0", 10))}
            className="rounded-sm h-9 mt-1 font-mono"
          />
        </div>
        <div>
          <label className="meta-label">Timezone</label>
          <select
            data-testid="schedule-tz-select"
            value={draft.timezone || "Asia/Kolkata"}
            onChange={(e) => set("timezone", e.target.value)}
            className="w-full rounded-sm h-9 mt-1 px-2 border border-input bg-background text-sm"
          >
            {COMMON_TZ.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="meta-label">Working Days</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {DAY_NAMES.map((d) => (
            <label key={d} className="flex items-center gap-1 text-xs cursor-pointer border border-border rounded-sm px-2 py-1" data-testid={`schedule-day-${d}`}>
              <Checkbox className="rounded-sm h-3.5 w-3.5" checked={draft.days?.includes(d)} onCheckedChange={() => toggleDay(d)} />
              {d}
            </label>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button data-testid="schedule-save-button" onClick={() => onSave(draft)} className="rounded-sm bg-primary hover:bg-[#00227A] h-9">
          <Check size={14} className="mr-1" /> Save Schedule
        </Button>
        <Button data-testid="schedule-cancel-button" variant="outline" onClick={onCancel} className="rounded-sm h-9">
          <X size={14} className="mr-1" /> Cancel
        </Button>
        {value && (
          <Button data-testid="schedule-clear-button" variant="outline" onClick={() => onSave(null)} className="rounded-sm h-9 ml-auto text-red-600">
            Clear Schedule
          </Button>
        )}
      </div>
    </div>
  );
}

function ScheduleChip({ schedule }) {
  if (!schedule || !schedule.start_time || !schedule.end_time) {
    return <span className="text-[10px] text-muted-foreground italic">Not assigned</span>;
  }
  return (
    <div className="text-xs">
      <div className="font-mono">{schedule.start_time}–{schedule.end_time}</div>
      <div className="text-[10px] text-muted-foreground">
        Break {schedule.break_minutes || 0}m · {schedule.timezone || "UTC"} · {(schedule.days || []).join("/") || "—"}
      </div>
    </div>
  );
}

export default function ShiftsTab() {
  const [view, setView] = useState("live"); // "live" | "reports"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [roleFilter, setRoleFilter] = useState(""); // "" = all

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shifts/by-agent");
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Refresh every 10s for live status
  useEffect(() => {
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  // Unique roles present in the table (sorted, with "" filtered out for the dropdown)
  const availableRoles = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.role) set.add(r.role); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!roleFilter) return rows;
    return rows.filter((r) => (r.role || "") === roleFilter);
  }, [rows, roleFilter]);

  const saveSchedule = async (uid, schedule) => {
    try {
      await api.put(`/users/${uid}`, { schedule: schedule || EMPTY_SCHEDULE_CLEAR() });
      toast.success(schedule ? "Schedule updated" : "Schedule cleared");
      setEditingUserId(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      {/* Sub-tab switcher: Live shifts (current behaviour) vs Reports (new). */}
      <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="shifts-subtab-switch">
        <button
          type="button"
          onClick={() => setView("live")}
          data-testid="shifts-subtab-live"
          className={`px-4 h-9 text-sm border-r border-border ${view === "live" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary/60"}`}
        >
          Live Shifts
        </button>
        <button
          type="button"
          onClick={() => setView("reports")}
          data-testid="shifts-subtab-reports"
          className={`px-4 h-9 text-sm ${view === "reports" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary/60"}`}
        >
          Reports
        </button>
      </div>

      {view === "reports" ? <ShiftReports /> : (
      <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={load} variant="outline" className="rounded-sm h-9" data-testid="shifts-refresh">
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <select
          data-testid="shifts-role-filter"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="rounded-sm h-9 px-3 border border-input bg-background text-sm"
        >
          <option value="">Show: All roles ({rows.length})</option>
          {availableRoles.map((r) => {
            const count = rows.filter((row) => (row.role || "") === r).length;
            return <option key={r} value={r}>{r} ({count})</option>;
          })}
        </select>
        {roleFilter && (
          <button
            type="button"
            onClick={() => setRoleFilter("")}
            className="text-xs text-muted-foreground hover:text-foreground underline"
            data-testid="shifts-clear-role-filter"
          >
            Clear filter
          </button>
        )}
        <span className="text-xs text-muted-foreground ml-2" data-testid="shifts-agent-count">
          {visibleRows.length}{roleFilter ? ` / ${rows.length}` : ""} user{visibleRows.length === 1 ? "" : "s"} · {visibleRows.filter((r) => r.active).length} live · {visibleRows.filter((r) => r.schedule?.start_time).length} scheduled
        </span>
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left border-b border-border">
              <th className="px-3 py-2 meta-label">User</th>
              <th className="px-3 py-2 meta-label">Role</th>
              <th className="px-3 py-2 meta-label">Schedule</th>
              <th className="px-3 py-2 meta-label">Status</th>
              <th className="px-3 py-2 meta-label">Latest Shift</th>
              <th className="px-3 py-2 meta-label">Idle</th>
              <th className="px-3 py-2 meta-label">Break</th>
              <th className="px-3 py-2 meta-label">Leads</th>
              <th className="px-3 py-2 meta-label text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">{rows.length === 0 ? "No users yet. Create one in the Users & Permissions tab." : `No users match the filter "${roleFilter}".`}</td></tr>
            )}
            {visibleRows.map((r) => {
              const isEdit = editingUserId === r.user_id;
              const s = r.latest_shift;
              const total = s ? durationSeconds(s.start_at, s.end_at) : 0;
              const live = !!s && !s.end_at;
              const flags = [];
              if (r.schedule_eval?.scheduled && r.schedule_eval?.outside_window) flags.push("outside-window");
              if (s?.late_start) flags.push("late-start");
              if (r.break_overrun) flags.push("break-overrun");
              return (
                <>
                  <tr key={r.user_id} className="border-b border-border last:border-0 align-top" data-testid={`agent-row-${r.user_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.name || "—"}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">{r.email}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.role ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm badge-neutral uppercase tracking-wider">{r.role}</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <ScheduleChip schedule={r.schedule} />
                    </td>
                    <td className="px-3 py-2">
                      {live ? (
                        s.on_break ? (
                          <span className="badge-warning px-2 py-0.5 rounded-sm text-[10px]">On break</span>
                        ) : (
                          <span className="badge-success px-2 py-0.5 rounded-sm text-[10px]">Live</span>
                        )
                      ) : s ? (
                        <span
                          className="badge-neutral px-2 py-0.5 rounded-sm text-[10px] inline-flex items-center gap-1"
                          data-testid={`status-ended-${r.user_id}`}
                          title={s.auto_closed ? `Auto-closed by system (${s.auto_closed_reason || "stale_heartbeat"})` : "Ended manually"}
                        >
                          Ended
                          {s.auto_closed && (
                            <span
                              className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200"
                              data-testid={`badge-auto-closed-${r.user_id}`}
                            >
                              auto
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="badge-neutral px-2 py-0.5 rounded-sm text-[10px]">Never logged in</span>
                      )}
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {flags.includes("outside-window") && (
                          <span className="badge-warning px-1.5 py-0.5 rounded-sm text-[9px] inline-flex items-center gap-1" data-testid={`flag-outside-${r.user_id}`}>
                            <AlertTriangle size={10} /> outside window
                          </span>
                        )}
                        {flags.includes("late-start") && (
                          <span className="badge-warning px-1.5 py-0.5 rounded-sm text-[9px] inline-flex items-center gap-1" data-testid={`flag-late-${r.user_id}`}>
                            <Clock size={10} /> late start
                          </span>
                        )}
                        {flags.includes("break-overrun") && (
                          <span className="badge-error px-1.5 py-0.5 rounded-sm text-[9px] inline-flex items-center gap-1" data-testid={`flag-break-overrun-${r.user_id}`}>
                            <Coffee size={10} /> break overrun
                          </span>
                        )}
                        {!flags.length && r.schedule_eval?.scheduled && (
                          <span className="badge-success px-1.5 py-0.5 rounded-sm text-[9px] inline-flex items-center gap-1">
                            <CheckCircle2 size={10} /> on schedule
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {s ? (
                        <>
                          <div>{fmt(total)}</div>
                          <div className="text-[10px] text-muted-foreground">{(s.start_at || "").slice(0, 19).replace("T", " ")}</div>
                        </>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-amber-700">{s ? fmt(s.idle_seconds) : "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-rose-700">{s ? fmt(s.break_seconds) : "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{s ? (s.leads_submitted || 0) : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      {!isEdit ? (
                        <button
                          data-testid={`edit-schedule-${r.user_id}`}
                          onClick={() => setEditingUserId(r.user_id)}
                          className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                        >
                          <Pencil size={12} /> Assign / Edit
                        </button>
                      ) : (
                        <button
                          onClick={() => setEditingUserId(null)}
                          className="text-muted-foreground hover:underline text-xs inline-flex items-center gap-1"
                        >
                          <X size={12} /> Close
                        </button>
                      )}
                    </td>
                  </tr>
                  {isEdit && (
                    <tr className="border-b border-border bg-secondary/20">
                      <td colSpan={9} className="px-3 py-4">
                        <ScheduleEditor
                          value={r.schedule}
                          onSave={(v) => saveSchedule(r.user_id, v)}
                          onCancel={() => setEditingUserId(null)}
                        />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
      )}
    </div>
  );
}

// Sentinel — sending an empty schedule clears it on the backend.
function EMPTY_SCHEDULE_CLEAR() {
  return { start_time: "", end_time: "", break_minutes: 0, timezone: "Asia/Kolkata", days: [] };
}
